import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error);
    console.error('Error details:', errorInfo.componentStack);
    this.setState({ errorInfo });
  }

  private handleRetry = () => {
    window.location.reload();
  };

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-dark-bg flex items-center justify-center p-4">
          <div className="max-w-lg w-full">
            {/* Error Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-red-500/10 border-2 border-red-500/50 flex items-center justify-center animate-pulse">
                <svg
                  className="w-10 h-10 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold text-center text-white mb-2">
              出错了
            </h1>
            <p className="text-gray-400 text-center mb-8">
              应用程序遇到了意外错误
            </p>

            {/* Error Card */}
            <div className="bg-dark-card border border-dark-border rounded-xl p-6 mb-6 shadow-lg">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-neon-cyan animate-pulse" />
                <span className="text-neon-cyan text-sm font-mono">
                  ERROR DETAILS
                </span>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-gray-500 text-xs mb-1">错误信息</p>
                  <p className="text-red-400 font-mono text-sm break-all">
                    {this.state.error?.message || 'Unknown error'}
                  </p>
                </div>

                {this.state.error?.name && (
                  <div>
                    <p className="text-gray-500 text-xs mb-1">错误类型</p>
                    <p className="text-neon-blue font-mono text-sm">
                      {this.state.error.name}
                    </p>
                  </div>
                )}

                {this.state.errorInfo && (
                  <div>
                    <p className="text-gray-500 text-xs mb-1">组件堆栈</p>
                    <pre className="text-gray-400 font-mono text-xs overflow-x-auto whitespace-pre-wrap max-h-32 overflow-y-auto bg-black/30 p-3 rounded-lg">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={this.handleRetry}
                className="flex-1 bg-neon-cyan/10 hover:bg-neon-cyan/20 border border-neon-cyan/50 hover:border-neon-cyan text-neon-cyan font-medium py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 group"
                aria-label="重新加载页面"
              >
                <svg
                  className="w-5 h-5 transition-transform group-hover:rotate-180"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                重新加载
              </button>

              <button
                onClick={this.handleReset}
                className="flex-1 bg-dark-card hover:bg-dark-card/80 border border-dark-border hover:border-neon-cyan/30 text-gray-300 hover:text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                aria-label="清除错误状态"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                清除错误
              </button>
            </div>

            {/* Debug Hint */}
            <p className="text-gray-600 text-xs text-center mt-6">
              如果问题持续存在，请检查浏览器控制台获取更多信息
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
